import { extractEmailAddress } from "@/lib/compose/composeDraft";

const LS_KEY = "mail_remote_images_allowed_senders_v1";
const MAX_SENDERS = 200;

export function senderEmailForRemoteImages(fromRaw: string): string {
  return extractEmailAddress(fromRaw).trim().toLowerCase();
}

function readAllowedSenders(): string[] {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((value): value is string => typeof value === "string" && value.includes("@"));
  } catch {
    return [];
  }
}

export function isSenderAllowedRemoteImages(fromRaw: string): boolean {
  const email = senderEmailForRemoteImages(fromRaw);
  if (!email.includes("@")) return false;
  return readAllowedSenders().includes(email);
}

export function allowSenderRemoteImages(fromRaw: string): void {
  const email = senderEmailForRemoteImages(fromRaw);
  if (!email.includes("@")) return;
  try {
    const next = [email, ...readAllowedSenders().filter((entry) => entry !== email)].slice(0, MAX_SENDERS);
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    /* quota / navigation privée */
  }
}

export function isRemoteImageUrl(raw: string): boolean {
  const href = raw.trim().toLowerCase();
  if (!href) return false;
  if (
    href.startsWith("data:") ||
    href.startsWith("blob:") ||
    href.startsWith("about:") ||
    href.startsWith("cid:")
  ) {
    return false;
  }
  if (href.includes("imap/attachment.php")) return false;
  return href.startsWith("https:") || href.startsWith("http:") || href.startsWith("//");
}

function isLeftoverCidUrl(raw: string): boolean {
  return raw.trim().toLowerCase().startsWith("cid:");
}

function visitVisualUrls(el: Element, visit: (url: string) => void): void {
  const src = el.getAttribute("src");
  if (src) visit(src);
  const href = el.getAttribute("href");
  if (href && el.localName === "image") visit(href);
  const srcset = el.getAttribute("srcset");
  if (srcset) {
    for (const part of srcset.split(",")) {
      const url = part.trim().split(/\s+/)[0];
      if (url) visit(url);
    }
  }
  const poster = el.getAttribute("poster");
  if (poster) visit(poster);
  const background = el.getAttribute("background");
  if (background) visit(background);
  const style = el.getAttribute("style");
  if (!style) return;
  const re = /url\(\s*(['"]?)([^'")]+)\1\s*\)/gi;
  let match: RegExpExecArray | null;
  while ((match = re.exec(style)) !== null) {
    visit(match[2].trim());
  }
}

function queryVisualNodes(doc: Document): NodeListOf<Element> {
  return doc.querySelectorAll(
    "img, source, video, input[type='image'], image, [background], [style*='url('], [style*='URL(']"
  );
}

export function htmlHasRemoteImages(html: string): boolean {
  const trimmed = html.trim();
  if (!trimmed) return false;

  if (typeof DOMParser === "undefined") {
    return /(?:src|srcset|poster|background)\s*=\s*["']?\s*(?:https?:|\/\/)/i.test(trimmed)
      || /url\(\s*['"]?(?:https?:|\/\/)/i.test(trimmed);
  }

  const doc = new DOMParser().parseFromString(trimmed, "text/html");
  let found = false;
  queryVisualNodes(doc).forEach((el) => {
    if (found) return;
    visitVisualUrls(el, (url) => {
      if (isRemoteImageUrl(url)) found = true;
    });
  });
  return found;
}

function rewriteStyleUrls(style: string, allowRemote: boolean): string {
  return style.replace(/url\(\s*(['"]?)([^'")]+)\1\s*\)/gi, (full, ...parts: string[]) => {
    const href = (parts[1] ?? "").trim();
    if (isLeftoverCidUrl(href) || (!allowRemote && isRemoteImageUrl(href))) {
      return "none";
    }
    return full;
  });
}

/** Affichage seul : masque cid morts + images http(s) tant que l’utilisateur n’a pas accepté. */
export function applyRemoteImagePolicy(html: string, allowRemote: boolean): string {
  const trimmed = html.trim();
  if (!trimmed || typeof DOMParser === "undefined") return trimmed;

  const hadDoctype = /^\s*<!DOCTYPE/i.test(trimmed);
  const doc = new DOMParser().parseFromString(trimmed, "text/html");

  const shouldDrop = (url: string): boolean =>
    isLeftoverCidUrl(url) || (!allowRemote && isRemoteImageUrl(url));

  queryVisualNodes(doc).forEach((el) => {
    const src = el.getAttribute("src");
    if (src && shouldDrop(src)) {
      el.removeAttribute("src");
    }
    const href = el.getAttribute("href");
    if (href && el.localName === "image" && shouldDrop(href)) {
      el.removeAttribute("href");
    }
    const srcset = el.getAttribute("srcset");
    if (srcset) {
      const urls = srcset.split(",").map((part) => part.trim().split(/\s+/)[0] ?? "");
      if (urls.some(shouldDrop)) {
        el.removeAttribute("srcset");
      }
    }
    const poster = el.getAttribute("poster");
    if (poster && shouldDrop(poster)) {
      el.removeAttribute("poster");
    }
    const background = el.getAttribute("background");
    if (background && shouldDrop(background)) {
      el.removeAttribute("background");
    }
    const style = el.getAttribute("style");
    if (style) {
      el.setAttribute("style", rewriteStyleUrls(style, allowRemote));
    }
  });

  const serialized = doc.documentElement.outerHTML;
  return hadDoctype ? `<!DOCTYPE html>\n${serialized}` : serialized;
}
